// Schedule › Team manager › Time off, Blueprint only (pages/ux-full schedule/timeoff), on ListPage
// (BL-189): one of Team manager's tabs with Who's working and Hours worked (TeamTabs.tsx). Who's off in
// the next 90 days is the list (RowList), the company's coming holidays under it in the same column
// (a Section of RowList), and Book time off beside it; what was just booked or cancelled is the
// notice under the head. Reads timeOff.list, holidays.list, staff.list and nonJobCodes.list, and
// re-reads on the board's live changes. Booking and cancelling are timeOff.add and timeOff.cancel, for
// those who may (owner, manager, dispatcher); while the company mirrors ServiceTitan, time off is
// ServiceTitan's.
import { useCallback, useMemo, useState } from 'react';
import { RpcError } from '@flowology/client';
import { dateText } from '@flowology/kernel';
import {
  Button,
  Checkbox,
  ConfirmDialog,
  holidayRows,
  RowList,
  SelectField,
  TextField,
  timeOffRows,
} from '@flowology/ui-web/fl';
import { ListPage, Section, type ListStatus } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { hoursRights, timeOffInput, type NonJobCode, type TimeOffForm } from '../settings/hours-model.ts';
import { useLive } from './live.ts';
import { pensBy, timeOffListProps, type TimeOffRow } from './pages-model.ts';
import { TeamTabs } from './TeamTabs.tsx';
import './team-manager.css';

const say = (e: unknown, fallback: string) => (e instanceof RpcError ? e.message : fallback);
const addDays = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

const blankForm = (from: string): TimeOffForm => ({
  code: '',
  partDay: false,
  from,
  to: '',
  start: '13:00',
  end: '17:00',
  note: '',
});

export function TimeOffScreen({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const today = todayIn(timeZone);
  const mirroring = useMirror();
  const may = hoursRights(nav.permissions ?? []).manage && !mirroring;
  const span = { from: today, to: addDays(today, 90), includeCancelled: true };
  const list = useQuery<{ timeOff: TimeOffRow[] }>('timeOff.list', span);
  const holidays = useQuery<{ year: number; holidays: { date: string; name: string; paid: boolean }[] }>(
    'holidays.list',
    { year: Number(today.slice(0, 4)) },
  );
  const techs = useQuery<{ staff: { id: string; displayName: string }[] }>('staff.list', {
    techniciansOnly: true,
  });
  const people = useQuery<{ staff: { id: string; displayName: string }[] }>('staff.list', {});
  const codes = useQuery<{ codes: NonJobCode[] }>('nonJobCodes.list', {});
  useLive(() => {
    void list.reload();
    void holidays.reload();
  });
  const crumb = usePageCrumb(nav);
  const pens = useMemo(() => pensBy(techs.data?.staff ?? []), [techs.data]);
  const leave = (codes.data?.codes ?? []).filter((c) => c.category === 'leave');
  const [who, setWho] = useState('');
  const [form, setForm] = useState<TimeOffForm>(() => blankForm(today));
  const [cancelling, setCancelling] = useState<TimeOffRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const built = who ? timeOffInput(who, form) : { problem: 'Pick who it’s for.' };
  const book = useCallback(async () => {
    if ('problem' in built) return setError(built.problem);
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<TimeOffRow>('timeOff.add', built.input);
      setSaid(`Booked for ${r.staffName}: ${r.text}.`);
      setForm(blankForm(today));
      await list.reload();
    } catch (e) {
      setError(say(e, 'Couldn’t book the time off. Try again.'));
    } finally {
      setBusy(false);
    }
  }, [built, list, today]);
  const booked = (list.data?.timeOff ?? []).filter((o) => !o.cancelledAt).length;
  const status: ListStatus = list.data ? 'ready' : list.error ? 'failed' : 'loading';
  const coming = (holidays.data?.holidays ?? []).filter((h) => h.date >= today);
  return (
    <div className="app">
      <TeamTabs nav={nav} />
      <main className="bp-team-main">
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Time off',
            lede: list.data
              ? `${booked} booked in the next 90 days. Booked time off comes off the board and the open times.`
              : 'Who is off and when. Booked time off comes off the board and the open times.',
          }}
          notice={
            said || mirroring ? (
              <>
                {said && (
                  <p className="bp-team-said" role="status">
                    {said}
                  </p>
                )}
                {mirroring && <p className="bp-team-sub">{MIRROR_LINE}</p>}
              </>
            ) : null
          }
          list={{
            title: 'Who’s off',
            status,
            what: 'the time off',
            failedText: list.error ?? 'The time off didn’t load.',
            onRetry: () => void list.reload(),
            children: list.data && (
              <RowList
                rows={timeOffRows(
                  timeOffListProps(list.data.timeOff, pens),
                  may
                    ? (id) => setCancelling(list.data?.timeOff.find((o) => o.id === id) ?? null)
                    : undefined,
                )}
                empty="Nobody is off. Time off booked here comes off the board and the open times."
              />
            ),
            after: (
              <Section title="Company holidays">
                {holidays.data ? (
                  coming.length ? (
                    <RowList
                      rows={holidayRows(
                        coming.slice(0, 5).map((h) => ({
                          day: dateText(h.date, 'UTC', { year: false, weekday: 'short' }),
                          name: h.name,
                          paid: h.paid,
                        })),
                      )}
                    />
                  ) : (
                    <p className="bp-team-sub">No more holidays this year. Setup › Settings adds them.</p>
                  )
                ) : (
                  <p className="bp-team-sub">{holidays.error ?? 'Getting the holidays…'}</p>
                )}
              </Section>
            ),
          }}
          aside={{
            title: 'Book time off',
            children: !may ? (
              <p className="bp-team-sub">
                {mirroring ? MIRROR_LINE : 'The owner, a manager or a dispatcher books time off.'}
              </p>
            ) : (
              <form
                className="bp-team-form"
                aria-label="Book time off"
                onSubmit={(e) => {
                  e.preventDefault();
                  void book();
                }}
              >
                <SelectField
                  label="Who"
                  value={who}
                  onChange={setWho}
                  options={[
                    { value: '', label: 'Pick someone…' },
                    ...(people.data?.staff ?? []).map((s) => ({ value: s.id, label: s.displayName })),
                  ]}
                />
                <SelectField
                  label="What for"
                  value={form.code}
                  onChange={(code) => setForm({ ...form, code })}
                  options={[
                    { value: '', label: 'Pick one…' },
                    ...leave.map((c) => ({ value: c.code, label: `${c.name}${c.paid ? '' : ' (unpaid)'}` })),
                  ]}
                />
                <Checkbox isSelected={form.partDay} onChange={(partDay) => setForm({ ...form, partDay })}>
                  Part of one day
                </Checkbox>
                <TextField
                  label={form.partDay ? 'On' : 'From'}
                  type="date"
                  value={form.from}
                  onChange={(from) => setForm({ ...form, from })}
                />
                {form.partDay ? (
                  <div className="bp-team-pair">
                    <TextField
                      label="Starts"
                      type="time"
                      value={form.start}
                      onChange={(start) => setForm({ ...form, start })}
                    />
                    <TextField
                      label="Ends"
                      type="time"
                      value={form.end}
                      onChange={(end) => setForm({ ...form, end })}
                    />
                  </div>
                ) : (
                  <TextField
                    label="To (leave it empty for one day)"
                    type="date"
                    value={form.to}
                    min={form.from}
                    onChange={(to) => setForm({ ...form, to })}
                  />
                )}
                <TextField
                  label="Note"
                  value={form.note}
                  maxLength={500}
                  onChange={(note) => setForm({ ...form, note })}
                />
                {error && (
                  <p className="bp-team-error" role="alert">
                    {error}
                  </p>
                )}
                <span>
                  <Button variant="primary" type="submit" isBusy={busy}>
                    {busy ? 'Booking…' : 'Book it'}
                  </Button>
                </span>
              </form>
            ),
          }}
        />
      </main>
      {/* Cancelling time off asks why (timeOff.cancel): it stays on the list, cancelled, with the reason. */}
      <ConfirmDialog
        isOpen={!!cancelling}
        onClose={() => setCancelling(null)}
        title={cancelling ? `Cancel ${cancelling.staffName}’s ${cancelling.label.toLowerCase()}` : ''}
        confirmLabel="Cancel it"
        cancelLabel="Keep it"
        reason={{ label: 'Why', missing: 'Say why it’s cancelled.' }}
        failText="Couldn’t cancel it. Try again."
        onConfirm={async (why) => {
          if (!cancelling) return;
          try {
            await client.call('timeOff.cancel', { id: cancelling.id, reason: why.trim() });
          } catch (e) {
            throw new Error(say(e, 'Couldn’t cancel it. Try again.'), { cause: e });
          }
          setSaid(`Cancelled: ${cancelling.text}.`);
          setCancelling(null);
          void list.reload();
        }}
      >
        {cancelling ? `${cancelling.text}. It stays on the list, cancelled, with why.` : null}
      </ConfirmDialog>
    </div>
  );
}
